import React from 'react';
import { ChevronDown } from 'lucide-react';

export type SelectSize = 'sm' | 'md' | 'lg';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: SelectSize;
  options: SelectOption[];
  placeholder?: string;
  error?: string;
}

const SIZE: Record<SelectSize, { select: string; icon: number }> = {
  sm: { select: 'h-8 text-xs', icon: 14 },
  md: { select: 'h-10 text-sm', icon: 16 },
  lg: { select: 'h-12 text-md', icon: 18 },
};

const Select: React.FC<SelectProps> = ({
  size = 'md',
  options,
  placeholder,
  error,
  className = '',
  id,
  ...rest
}) => {
  const sz = SIZE[size];
  const errorId = error ? `${id}-error` : undefined;
  const borderColor = error ? 'border-error' : 'border-default';
  return (
    <div className="w-full">
      <div className="relative">
        <select
          id={id}
          aria-invalid={!!error}
          aria-describedby={errorId}
          className={`w-full bg-input ${borderColor} border rounded-md pl-3 pr-9 ${sz.select} text-pt-forest focus:outline-none focus:ring-2 focus:ring-pt-gold focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed appearance-none transition-shadow duration-[var(--motion-duration-fast)] ${className}`.trim()}
          {...rest}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={sz.icon}
          strokeWidth={1.75}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
          aria-hidden
        />
      </div>
      {error && (
        <p id={errorId} className="text-xs text-error mt-1">
          {error}
        </p>
      )}
    </div>
  );
};

export default Select;
